import { Link, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { PasswordInput } from '@/shared/ui/password-input'
import { PasswordPolicyHints } from '@/shared/ui/password-policy-hints'
import { Label } from '@/shared/ui/label'
import { t } from '@/shared/lib/i18n'
import { newSessionPath, passwordResetPath, verifyCodePasswordResetPath } from '@/shared/lib/routes'
import { AuthShell } from './AuthShell'

interface ResetPasswordPageProps {
  flow?: 'activation' | 'forgot_token'
  email: string
  resetToken?: string
  confirmationCode?: string
  errors?: Record<string, string[]>
  flash?: { alert?: string; notice?: string }
  [key: string]: unknown
}

const ResetPassword = () => {
  const {
    flow = 'activation',
    email,
    resetToken = '',
    confirmationCode: initialConfirmationCode = '',
    errors: serverErrors,
    flash,
  } = usePage<ResetPasswordPageProps>().props

  const codeForm = useForm({
    email,
    confirmation_code: initialConfirmationCode,
  })

  const passwordForm = useForm({
    password: '',
    password_confirmation: '',
  })

  const isActivationCodeStep = flow === 'activation'
  const codeFormErrors = codeForm.errors as typeof codeForm.errors & { base?: string }
  const formFieldErrors = passwordForm.errors as typeof passwordForm.errors & {
    base?: string
    passwordConfirmation?: string
  }
  const passwordConfirmationError =
    formFieldErrors.passwordConfirmation ?? serverErrors?.passwordConfirmation?.[0]
  const errorMessage = isActivationCodeStep
    ? (codeFormErrors.base ?? serverErrors?.base?.[0] ?? flash?.alert)
    : passwordConfirmationError
      ? undefined
      : (formFieldErrors.base ?? serverErrors?.base?.[0] ?? flash?.alert)

  const handleCodeSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    codeForm.post(verifyCodePasswordResetPath())
  }

  const handlePasswordSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    passwordForm.patch(`${passwordResetPath()}?token=${encodeURIComponent(resetToken)}`)
  }

  return (
    <AuthShell pageTitle={t('auth.resetPassword.title')}>
      <h1 className="text-2xl font-bold">{t('auth.resetPassword.title')}</h1>

      <p className="text-sm text-muted-foreground">{email}</p>

      {flash?.notice && (
        <div className="rounded-md border border-emerald-500/50 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash.notice}
        </div>
      )}

      {errorMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      {isActivationCodeStep ? (
        <form onSubmit={handleCodeSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="confirmation_code">{t('auth.resetPassword.codeLabel')}</Label>
            <Input
              id="confirmation_code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={codeForm.data.confirmation_code}
              onChange={(event) => codeForm.setData('confirmation_code', event.target.value)}
              required
            />
          </div>
          <div className="pt-4">
            <Button
              type="submit"
              size="lg"
              className="h-12 w-full text-base"
              disabled={codeForm.processing}
            >
              {codeForm.processing
                ? t('auth.resetPassword.verifying')
                : t('auth.resetPassword.continue')}
            </Button>
          </div>
          <div className="text-center">
            <Link
              href={newSessionPath()}
              className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              {t('auth.resetPassword.backToLogin')}
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.resetPassword.passwordLabel')}</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              aria-describedby="reset-password-hints"
              value={passwordForm.data.password}
              onChange={(event) => passwordForm.setData('password', event.target.value)}
              required
            />
            <PasswordPolicyHints id="reset-password-hints" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password_confirmation">{t('auth.resetPassword.confirmLabel')}</Label>
            <PasswordInput
              id="password_confirmation"
              autoComplete="new-password"
              value={passwordForm.data.password_confirmation}
              onChange={(event) =>
                passwordForm.setData('password_confirmation', event.target.value)
              }
              required
            />
            {passwordConfirmationError && (
              <p className="text-sm text-destructive">{passwordConfirmationError}</p>
            )}
          </div>
          <div className="pt-6">
            <Button
              type="submit"
              size="lg"
              className="h-12 w-full text-base"
              disabled={passwordForm.processing}
            >
              {passwordForm.processing
                ? t('auth.resetPassword.submitting')
                : t('auth.resetPassword.submit')}
            </Button>
          </div>
          <div className="text-center">
            <Link
              href={newSessionPath()}
              className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              {t('auth.resetPassword.backToLogin')}
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  )
}

export default ResetPassword
